"use client"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"
import * as React from "react"

import { cn } from "../../utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>(
	({ className, ...props }, ref) => (
		<DialogPrimitive.Overlay
			ref={ref}
			className={cn(
				"data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-1300 bg-black/40 backdrop-blur-lg",
				className
			)}
			{...props}
		/>
	)
)
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
		/** 우측 상단 닫기(X) 버튼 노출 여부 */
		showCloseButton?: boolean
		/**
		 * 닫기(X) 버튼의 접근성 라벨. 패키지는 앱의 i18n에 접근할 수 없으므로 앱이 번역된 문자열을
		 * 직접 주입할 수 있게 한다.
		 * @default "Close dialog"
		 */
		closeLabel?: string
	}
>(({ className, children, showCloseButton = true, closeLabel = "Close dialog", ...props }, ref) => {
	const handleOpenAutoFocus = (e: Event) => {
		// 모달이 열릴 때 포커스를 모달 내부로 이동
		const target = e.currentTarget as HTMLElement
		if (target) {
			// 첫 번째 포커스 가능한 요소 찾기 (close button은 제외)
			const focusableElement = target.querySelector<HTMLElement>(
				'button:not(.close-button):not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)
			if (focusableElement) {
				e.preventDefault()
				// 다음 프레임에서 포커스 설정 (렌더링 완료 후)
				requestAnimationFrame(() => {
					focusableElement.focus()
				})
			}
			// 포커스 가능한 요소가 없으면 Radix UI의 기본 동작 사용
		}
	}

	return (
		<DialogPortal>
			<DialogOverlay />
			<DialogPrimitive.Content
				ref={ref}
				className={cn(
					"bg-white/90 dark:bg-g250/60 backdrop-blur-[40px] border border-white/10 gap-7 rounded-xl p-7 shadow-xl duration-200 w-full",
					"data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
					"data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
					"fixed top-[50%] left-[50%] z-1300 grid w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%]",
					"focus:outline-none focus:ring-0 focus:ring-offset-0",
					className
				)}
				onOpenAutoFocus={handleOpenAutoFocus}
				{...props}
			>
				{children}
				{showCloseButton && (
					<DialogPrimitive.Close
						className="close-button absolute w-8 h-8 p-2 flex items-center justify-center top-6.5 right-6.5 rounded-sm text-text bg-transparent hover:bg-white/20 cursor-pointer [&_svg]:pointer-events-none [&_svg]:shrink-0 focus:outline-none focus:ring-0 focus:ring-offset-0"
						aria-label={closeLabel}
					>
						<XIcon className="w-5 h-5" aria-hidden="true" />
						<span className="sr-only">Close</span>
					</DialogPrimitive.Close>
				)}
			</DialogPrimitive.Content>
		</DialogPortal>
	)
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogFullscreenContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>>(
	({ className, children, ...props }, ref) => {
		const handleOpenAutoFocus = (e: Event) => {
			// 모달이 열릴 때 포커스를 모달 내부로 이동
			const target = e.currentTarget as HTMLElement
			if (target) {
				// 첫 번째 포커스 가능한 요소 찾기
				const focusableElement = target.querySelector<HTMLElement>(
					'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
				)
				if (focusableElement) {
					e.preventDefault()
					// 다음 프레임에서 포커스 설정 (렌더링 완료 후)
					requestAnimationFrame(() => {
						focusableElement.focus()
					})
				}
			}
		}

		return (
			<DialogPortal>
				<DialogPrimitive.Content
					ref={ref}
					className={cn(
						"bg-white/90 dark:bg-black/60 backdrop-blur-[40px] flex flex-col p-0 border-0 rounded-none fixed inset-0 w-screen h-dvh max-w-none duration-200 z-1300",
						"data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
						"data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
						"[&_.contents-wrap]:flex-grow [&_.contents-wrap]:overflow-hidden",
						"[&_.close]:absolute [&_.close]:top-6 [&_.close]:right-6",
						"focus:outline-none focus:ring-0 focus:ring-offset-0",
						className
					)}
					onOpenAutoFocus={handleOpenAutoFocus}
					onCloseAutoFocus={(e) => e.preventDefault()}
					onInteractOutside={(e) => e.preventDefault()}
					{...props}
				>
					{children}
				</DialogPrimitive.Content>
			</DialogPortal>
		)
	}
)
DialogFullscreenContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-col gap-2 text-left", className)} {...props} />
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-row justify-end items-center gap-2", className)} {...props} />
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(
	({ className, ...props }, ref) => <DialogPrimitive.Title ref={ref} className={cn("text-xl font-bold", className)} {...props} />
)
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(
	({ className, ...props }, ref) => <DialogPrimitive.Description ref={ref} className={cn("text-base", className)} {...props} />
)
DialogDescription.displayName = DialogPrimitive.Description.displayName

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogFullscreenContent, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger }
